import buildCreateInterview from '@Application/Beneficiaries/Interviews/buildCreateInterview';
import createInterview from '@Application/Beneficiaries/Interviews/createInterview';
import type { LocalInterview, LocalInterviewPort } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type { ActionAsFunction, DefaultPayload } from '@verclam/synapse';

/** The `dispatch` `useDispatch()` hands out: it only accepts a thunk, never a bare action. */
type EnsureDispatch = <A extends DefaultPayload>(thunk: ActionAsFunction<A>) => Promise<A>;

export type EnsureServerInterviewResult =
	| { readonly type: 'ready'; readonly interviewId: number; readonly recordingId: number }
	| { readonly type: 'invalid-context' }
	| { readonly type: 'missing-recording' }
	| { readonly type: 'rejected'; readonly action: DefaultPayload };

/**
 * Returns the server identity of a local interview, creating the interview on the server first when
 * it has none yet, and writing the new identity back to the local record. A rejected dispatch or a
 * failing port call propagates to the caller.
 */
const ensureServerInterview = async (
	dispatch: EnsureDispatch,
	localInterviewPort: LocalInterviewPort,
	localInterview: LocalInterview,
): Promise<EnsureServerInterviewResult> => {
	if (localInterview.interviewId !== null) {
		if (localInterview.recordingId === null) {
			return { type: 'missing-recording' };
		}

		return {
			type: 'ready',
			interviewId: localInterview.interviewId,
			recordingId: localInterview.recordingId,
		};
	}

	const command = buildCreateInterview(localInterview);

	if (command === null) {
		return { type: 'invalid-context' };
	}

	const action = await dispatch(createInterview(command));

	if (action.type !== 'INTERVIEW_CREATED') {
		return { type: 'rejected', action };
	}

	const createdRecording = action.payload.recording;

	if (createdRecording === null) {
		return { type: 'missing-recording' };
	}

	await localInterviewPort.attachServerIdentity(localInterview.id, {
		interviewId: action.payload.id,
		recordingId: createdRecording.id,
	});

	return { type: 'ready', interviewId: action.payload.id, recordingId: createdRecording.id };
};

export default ensureServerInterview;
